import { Component, EventEmitter, OnInit, Output, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as langs from '../../../../data/codingEditorlanguages.json';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { DashboardService } from '../../dashboard.service';
import { LoaderService } from 'src/app/services/loader.service';

// Types for better type safety
interface Topic {
  _id: string;
  topic: string;
  name?: string;
  title?: string;
  displayName?: string;
}

interface Language {
  id: string;
  name: string;
}

interface FilterForm {
  searchTerm: string;
  difficultyLevel: string[];
  topics: Topic[];
  languages: Language[];
  subTopics: string[];
}

interface DefaultFilters {
  difficultyLevel?: string[];
  topicRefs?: string[];
  languages?: string[];
  subTopics?: string[];
}

@Component({
  selector: 'app-question-filters',
  templateUrl: './question-filters.component.html',
  styleUrls: ['./question-filters.component.scss']
})

export class QuestionFiltersComponent implements OnInit, OnChanges {

  @Input() activeTabIndex: number = 0; // 0: MCQ, 1: OEQ, 2: Coding
  @Input() defaultFilters: DefaultFilters = {}; // Default filter values from API payload
  @Output() applyFilters = new EventEmitter<any>();
  @Output() clearFilters = new EventEmitter<void>();
  @Output() search = new EventEmitter<string>();

  questionsFilterFrom: UntypedFormGroup;
  difficultyLevels: string[] = [];
  topics: Topic[] = [];
  languages: Language[] = [];
  // Controls when to show the Clear Filters button
  showClearButton: boolean = false;

  // UI State for premium design
  isTopicExpanded: boolean = false;

  // Constants for better maintainability
  private static readonly TOPICS_LIMIT = 100;
  private static readonly ERROR_TIMEOUT = 3000;

  topicsSettings = {
    singleSelection: false,
    idField: '_id',
    textField: 'topic',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true,
  };

  languagesSettings = {
    singleSelection: false,
    idField: 'id',
    textField: 'name',
    selectAllText: 'Select All',
    unSelectAllText: 'UnSelect All',
    allowSearchFilter: true,
  };

  constructor(private fb: UntypedFormBuilder,
    private dashboardService: DashboardService,
    private toastr: ToastrService,
    private loaderService: LoaderService) {
    this.questionsFilterFrom = this.fb.group({
      searchTerm: [''],
      difficultyLevel: [''],
      topics: [''],
      languages: [''],
      subTopics: ['']
    });
  }

  ngOnInit(): void {
    this.getFilterValues();
    this.getTopics();
    this.loadLanguages();

    // Set initial default filters if provided
    if (this.defaultFilters) {
      this.setDefaultFilterValues(this.defaultFilters);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle default filters when they change
    if (changes['defaultFilters'] && !changes['defaultFilters'].firstChange) {
      this.setDefaultFilterValues(changes['defaultFilters'].currentValue);
    }

    if (changes['activeTabIndex'] && !changes['activeTabIndex'].firstChange) {
      this.handleTabChange(changes['activeTabIndex'].currentValue);
    }
  }

  /**
   * Handle tab change by clearing irrelevant filter selections
   */
  private handleTabChange(newTabIndex: number): void {
    if (newTabIndex === 2) {
      // Switched to Coding: clear Topics selection to avoid stale UI
      this.questionsFilterFrom.get('topics')?.setValue([]);
    } else {
      // Switched away from Coding: clear Languages selection
      this.questionsFilterFrom.get('languages')?.setValue([]);
    }
  }

  /**
   * Set default filter values from API payload
   */
  private setDefaultFilterValues(defaultFilters: DefaultFilters): void {
    if (!defaultFilters) return;

    // Set difficulty level if provided
    if (defaultFilters.difficultyLevel?.length > 0) {
      this.questionsFilterFrom.get('difficultyLevel')?.setValue(defaultFilters.difficultyLevel);
    }

    // Set topics if provided and not coding questions
    if (defaultFilters.topicRefs?.length > 0 && this.activeTabIndex !== 2) {
      if (this.topics.length > 0) {
        // Topics are loaded, map and set them
        const topicObjects = this.mapTopicIdsToObjects(defaultFilters.topicRefs);
        this.questionsFilterFrom.get('topics')?.setValue(topicObjects);
      }
      // If topics not loaded yet, they will be set when getTopics completes
    }

    // Set languages if provided and coding questions
    if (defaultFilters.languages?.length > 0 && this.activeTabIndex === 2) {
      const languageObjects = this.mapLanguageIdsToObjects(defaultFilters.languages as any);
      this.questionsFilterFrom.get('languages')?.setValue(languageObjects);
    }

    // Set sub-topics if provided
    if (defaultFilters.subTopics) {
      this.questionsFilterFrom.get('subTopics')?.setValue(defaultFilters.subTopics);
    }
  }

  /**
   * Map topic IDs to topic objects for proper display in dropdown
   */
  private mapTopicIdsToObjects(topicIds: string[]): Topic[] {
    if (!topicIds || !this.topics) return [];

    return topicIds.map(id => {
      const topic = this.topics.find((t: Topic) => t._id === id);

      if (topic) {
        // Ensure the topic object has the required properties for display
        return {
          _id: topic._id,
          topic: topic.topic || topic.name || topic.title || topic.displayName || id,
          ...topic // Include all other properties
        };
      }

      // Fallback if topic not found
      return { _id: id, topic: id };
    }).filter(Boolean);
  }

  /**
   * Map language IDs to language objects so dropdown shows names instead of IDs
   */
  private mapLanguageIdsToObjects(languageIds: Array<string | number | Language>): Language[] {
    if (!Array.isArray(languageIds)) { return []; }

    return languageIds.map((item: any) => {
      // If an object with a name is already provided, keep it
      if (item && typeof item === 'object' && ('name' in item)) {
        return item as Language;
      }

      const idNum = typeof item === 'string' ? Number(item) : Number(item);
      const match = this.languages.find((l: Language) => Number(l.id) === idNum);
      if (match) { return match; }

      // Fallback – create a minimal object so UI has a label
      return { id: (item as any), name: String(item) } as Language;
    }).filter(Boolean) as Language[];
  }

  private getTopics(): void {
    // Use getTopicsUsedInQuestions to ensure we only show topics that are actually used in questions
    // This fixes the bug where users couldn't find topics that were displayed on questions
    this.dashboardService.getTopicsUsedInQuestions().subscribe({
      next: (response: any) => {
        // Handle both array response or object with topics property
        this.topics = response.topics || response || [];
        // If we have pending default filters, set them now
        if (this.defaultFilters?.topicRefs?.length > 0 && this.activeTabIndex !== 2) {
          const topicObjects = this.mapTopicIdsToObjects(this.defaultFilters.topicRefs);
          this.questionsFilterFrom.get('topics')?.setValue(topicObjects);
        }
      },
      error: (error: any) => {
        this.toastr.error('Failed to load topics', 'Error', { timeOut: QuestionFiltersComponent.ERROR_TIMEOUT });
        // Set empty array as fallback
        this.topics = [];
      }
    });
  }

  private loadLanguages(): void {
    // Static list from JSON to avoid API dependency and match elsewhere
    this.languages = (langs as any)?.default || (langs as any);
    // If form currently holds language IDs (from defaults), convert them to objects now
    const current = this.questionsFilterFrom.get('languages')?.value;
    if (Array.isArray(current) && current.length && (typeof current[0] !== 'object' || !current[0]?.name)) {
      const languageObjects = this.mapLanguageIdsToObjects(current);
      this.questionsFilterFrom.get('languages')?.setValue(languageObjects);
    }
  }

  private getFilterValues(): void {
    this.loaderService.show('Loading filter options');
    this.dashboardService.getFilterValues().subscribe({
      next: (response: any) => {
        this.difficultyLevels = response.difficultyLevel || [];
        this.loaderService.hide();
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.errors?.[0]?.error || 'Failed to load filter values', 'Error', {
          timeOut: QuestionFiltersComponent.ERROR_TIMEOUT
        });
        this.loaderService.hide();
      }
    });
  }

  onApply() {
    const raw: FilterForm = this.questionsFilterFrom.value;

    // Determine if there are any filter values (ignoring search term).
    const hasValues = this.hasAnyFilterValues(raw);

    if (this.activeTabIndex === 2) {
      // Coding questions: use languages, exclude topics
      const languageIds = (raw.languages || []).map((l: Language) => l?.id ?? l).filter(Boolean);
      this.applyFilters.emit({
        ...raw,
        languages: languageIds,
        topics: [],
        subTopics: raw.subTopics || []
      });
    } else {
      // MCQ/OEQ questions: use topics, exclude languages
      const topicIds = (raw.topics || []).map((t: Topic) => t?._id ?? t).filter(Boolean);
      this.applyFilters.emit({
        ...raw,
        topics: topicIds,
        languages: [],
        subTopics: raw.subTopics || []
      });
    }

    // Show clear button only after Apply is clicked and values exist
    this.showClearButton = hasValues;
  }

  onClear() {
    // Reset form to empty values
    this.questionsFilterFrom.patchValue({
      searchTerm: '',
      difficultyLevel: [],
      topics: [],
      languages: [],
      subTopics: []
    });

    // Clear any default filters that might have been set
    this.defaultFilters = {};

    this.clearFilters.emit();
    // Hide the clear button after clearing
    this.showClearButton = false;
  }

  onSearch() {
    this.search.emit(this.questionsFilterFrom.get('searchTerm')?.value || '');
  }

  /**
   * Clear search text and trigger a search with empty term
   */
  onClearSearch() {
    this.questionsFilterFrom.get('searchTerm')?.setValue('');
    this.search.emit('');
  }

  /**
   * Returns true if any filter field has values (ignores search term)
   */
  private hasAnyFilterValues(raw: FilterForm): boolean {
    const hasDifficulty = Array.isArray(raw?.difficultyLevel)
      ? raw.difficultyLevel.length > 0
      : !!raw?.difficultyLevel;

    const hasTopics = Array.isArray(raw?.topics) && raw.topics.length > 0;
    const hasLanguages = Array.isArray(raw?.languages) && raw.languages.length > 0;
    const hasSubTopics = Array.isArray(raw?.subTopics) && raw.subTopics.length > 0;

    return hasDifficulty || hasTopics || hasLanguages || hasSubTopics;
  }

  /**
   * Toggle UI state for premium design
   */
  onMultiSelectOpen() {
    this.isTopicExpanded = true;
  }

  onMultiSelectClose() {
    this.isTopicExpanded = false;
  }
}
